import { useState, useEffect, useCallback } from "react"
import { Invites } from "../Apis/invitesApis"
import { message } from "antd"
import { getUserCookies } from "../utils/Cookies"
import { jwtDecode } from "jwt-decode"
import { translations } from "../utils/Translation/staticTranslation"

const useUserInvites = (selectMerchant, businessId, loading) => {
  const decoded =
    getUserCookies()?.token && jwtDecode(getUserCookies()?.token)
  const highest_role = decoded?.highest_role
  const market_role = decoded?.market_role
  /** Same scope as Users page: global merchant-user list (not per-business OWNER). */
  const canUseGlobalUserList =
    highest_role === "SUPERADMIN" ||
    market_role === "2" ||
    market_role === "3"
  const lang = localStorage.getItem("language") || "ar"

  const { common_error_msg } = translations[lang]
  const [userInvites, setUserInvites] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [filterStatus, setFilterStatus] = useState(undefined)
  const [initialLoading, setinitialLoading] = useState(false)
  const [userStatus, setUserStatus] = useState("")
  const [updateLoading, setUpdateLoading] = useState({})
  const [disableAutoFetch, setDisableAutoFetch] = useState(false) // Flag to control automatic fetching
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    next: null,
    previous: null,
  })

  // Fetch invites
  const fetchInvites = useCallback(
    async (
      merchant = selectMerchant,
      category = selectedCategory,
      search = searchTerm,
      filter = filterStatus,
      page = 1
    ) => {
      setinitialLoading(true)
      try {
        const payload = {}

        // Handle search by category
        if (category === "Name" && search) {
          payload.name = search
        } else if (category === "Role" && search) {
          payload.role = search
        }

        // Add filter status if present
        if (filter) {
          payload.status = filter
        }

        // Only add business ID if a specific merchant is selected and it's not 'all'
        if (merchant && typeof merchant === "object" && canUseGlobalUserList) {
          payload.business = merchant.id
        }

        // Add page parameter
        if (page) {
          payload.page = page
        }

        const response = await Invites.getUserList(payload)
        if (response.status === 200) {
          const data = response.data
          // Fix: Extract results from paginated response
          setUserInvites(data.results || data || [])
          
          // Update pagination state if paginated response
          if (data.count !== undefined) {
            setPagination({
              current: page,
              pageSize: 10,
              total: data.count || 0,
              next: data.next || null,
              previous: data.previous || null,
            })
          }
        }
      } catch (error) {
        console.error("Error fetching invites:", error)
      } finally {
        setinitialLoading(false)
      }
    },
    [
      selectMerchant,
      selectedCategory,
      searchTerm,
      filterStatus,
      highest_role,
      canUseGlobalUserList,
    ]
  )

  // Delete invited user
  const handleDeleteInvitedUser = async (userId) => {
    if (!userId) {
      message.error(common_error_msg?.invalid_user_id)
      return
    }
    setDeleteLoading(true)
    try {
      const response = await Invites.deleteInvitedUser(userId)
      if (response.status === 200) {
        message.success(common_error_msg?.user_deleted_successfully)
        setUserInvites((invites) =>
          invites.filter((user) => user.id !== userId)
        )
      }
    } catch (error) {
      message.error(common_error_msg?.failed_to_delete_user)
      console.error("Error deleting user:", error)
    } finally {
      setDeleteLoading(false)
    }
  }

  // Update user status
  const handleUpdateUserStatus = async (row, status) => {
    const payload = {
      is_active: status,
    }
    try {
      const UserID = row?.id
      setUpdateLoading((prev) => ({ ...prev, [UserID]: true }))

      const response = await Invites.updateInvitedUserStatus(payload, row.id)
      if (response.status === 200) {
        message.success(
          lang === "en" ? response.data.msg : response.data.msgArabic
        )
        setUserInvites((invites) =>
          invites.map((user) =>
            user.id === row.id ? { ...user, status: status } : user
          )
        )
      }
    } catch (error) {
      message.error("Failed to update user status")
      console.error("Error updating user status:", error)
    } finally {
      setUpdateLoading((prev) => ({ ...prev, [row?.id]: false }))
    }
  }

  // Initial fetch on mount
  useEffect(() => {
    if (!loading && !disableAutoFetch) {
      if (canUseGlobalUserList) {
        const merchant =
          selectMerchant === "all" || selectMerchant == null || selectMerchant === ""
            ? null
            : selectMerchant
        fetchInvites(merchant, "", "", "", 1)
      } else if (businessId) {
        // For other roles, fetch based on business ID
        fetchInvites(null, "", "", "", 1)
      }
    }
  }, [
    businessId,
    loading,
    fetchInvites,
    selectMerchant,
    highest_role,
    canUseGlobalUserList,
    disableAutoFetch,
  ])

  // Removed automatic search effect to prevent double fetching
  // This will now be controlled directly from the component

  return {
    updateLoading,
    selectedCategory,
    setUserStatus,
    handleUpdateUserStatus,
    initialLoading,
    userInvites,
    fetchInvites,
    handleDeleteInvitedUser,
    searchTerm,
    setSearchTerm,
    setSelectedCategory,
    filterStatus,
    setFilterStatus,
    setUserInvites,
    disableAutoFetch,
    setDisableAutoFetch,
    deleteLoading,
    pagination,
    setPagination,
  }
}

export default useUserInvites
